import { IonModalProps, IonModalStore } from "@/types/global";
import { ComponentRef, ModalOptions } from "@ionic/core";
import { modalController } from "@ionic/vue";
import { defineStore } from "pinia";

const defaultModalProps = {
  ModalTitle: undefined,
  ModalColor: "primary",
  IsDismissable: true,
  Breakpoints: undefined,
  InitialBreakpoint: undefined,
  CustomCssClass: "",
  ExpandToScroll: false,
  UseContentHeight: false,
  onModalDismiss: () => {},
};

const sheetModalProps = {
  Breakpoints: [0, 1],
  InitialBreakpoint: 1,
};

// AEM v2 — optional `Animated` passthrough (design: full-page drawers like the
// onboarding checklist appear in place; the default bottom-sheet slide travels
// the whole viewport and reads as heavy). Defaults to true so every existing
// modal keeps its current behavior.
type AnimatableModalProps = Partial<IonModalProps> & { Animated?: boolean };

function getInitialIonModalStore(): IonModalStore {
  const initialIonModalStore: IonModalStore = {
    ModalRef: null,
    StackModalRef: null,
    ModalProps: null,
    StackModalProps: null,
    IsModalPresented: false,
  };
  return JSON.parse(JSON.stringify(initialIonModalStore));
}

export const useIonModalStore = defineStore("ionModalStore", {
  state: (): IonModalStore => getInitialIonModalStore(),
  actions: {
    async openDefaultModal(
      childComponent: ComponentRef,
      componentProps: any = {},
      modalProps: AnimatableModalProps = {},
    ) {
      this.ModalProps = { ...defaultModalProps, ...modalProps };
      this.ModalProps.CustomCssClass = `${modalProps.CustomCssClass} custom-default-modal`;
      const modalOptions = {
        component: childComponent,
        componentProps: componentProps,
        cssClass: this.ModalProps.CustomCssClass,
        canDismiss: this.ModalProps.IsDismissable,
        backdropDismiss: this.ModalProps.IsDismissable,
        initialBreakpoint: this.ModalProps.InitialBreakpoint,
        breakpoints: this.ModalProps.Breakpoints,
        expandToScroll: this.ModalProps.ExpandToScroll,
        animated: modalProps.Animated ?? true,
        handle: false,
      };
      await this.openModal(modalOptions);
    },
    async openInlineModal(
      childComponent: ComponentRef,
      componentProps: any = {},
      modalProps: AnimatableModalProps = {},
    ) {
      this.ModalProps = { ...defaultModalProps, ...modalProps };
      this.ModalProps.CustomCssClass = `${modalProps.CustomCssClass} custom-inline-modal`;
      const modalOptions = {
        component: childComponent,
        componentProps: componentProps,
        cssClass: this.ModalProps.CustomCssClass,
        canDismiss: this.ModalProps.IsDismissable,
        animated: modalProps.Animated ?? true,
        handle: false,
      };
      await this.openModal(modalOptions);
    },
    async openSheetModal(
      childComponent: ComponentRef,
      componentProps: any = {},
      modalProps: AnimatableModalProps = {},
    ) {
      this.ModalProps = { ...defaultModalProps, ...sheetModalProps, ...modalProps };
      this.ModalProps.CustomCssClass = `${this.ModalProps.CustomCssClass} custom-sheet-modal`;
      const modalOptions = {
        component: childComponent,
        componentProps: componentProps,
        cssClass: this.ModalProps.CustomCssClass,
        canDismiss: this.ModalProps.IsDismissable,
        backdropDismiss: this.ModalProps.IsDismissable,
        initialBreakpoint: this.ModalProps.InitialBreakpoint,
        breakpoints: this.ModalProps.Breakpoints,
        expandToScroll: this.ModalProps.ExpandToScroll,
        animated: modalProps.Animated ?? true,
        handle: false,
      };
      await this.openModal(modalOptions);
    },
    async openStackedDefaultModal(
      childComponent: ComponentRef,
      componentProps: any = {},
      modalProps: AnimatableModalProps = {},
    ) {
      this.StackModalProps = { ...defaultModalProps, ...modalProps };
      const customCssClass = `${this.StackModalProps.CustomCssClass} custom-default-modal`;
      const modalOptions = {
        component: childComponent,
        componentProps: componentProps,
        cssClass: customCssClass,
        canDismiss: this.StackModalProps.IsDismissable,
        backdropDismiss: this.StackModalProps.IsDismissable,
        initialBreakpoint: this.StackModalProps.InitialBreakpoint,
        breakpoints: this.StackModalProps.Breakpoints,
        expandToScroll: this.StackModalProps.ExpandToScroll,
        animated: modalProps.Animated ?? true,
        handle: false,
      };
      await this.openStackedModal(modalOptions);
    },
    async openStackedInlineModal(
      childComponent: ComponentRef,
      componentProps: any = {},
      modalProps: AnimatableModalProps = {},
    ) {
      this.StackModalProps = { ...defaultModalProps, ...modalProps };
      const customCssClass = `${this.StackModalProps.CustomCssClass} custom-inline-modal`;
      const modalOptions = {
        component: childComponent,
        componentProps: componentProps,
        cssClass: customCssClass,
        canDismiss: this.StackModalProps.IsDismissable,
        backdropDismiss: this.StackModalProps.IsDismissable,
        initialBreakpoint: this.StackModalProps.InitialBreakpoint,
        breakpoints: this.StackModalProps.Breakpoints,
        expandToScroll: this.StackModalProps.ExpandToScroll,
        animated: modalProps.Animated ?? true,
        handle: false,
      };
      await this.openStackedModal(modalOptions);
    },
    async openStackedSheetModal(
      childComponent: ComponentRef,
      componentProps: any = {},
      modalProps: AnimatableModalProps = {},
    ) {
      this.StackModalProps = { ...defaultModalProps, ...sheetModalProps, ...modalProps };
      const customCssClass = `${this.StackModalProps.CustomCssClass} custom-sheet-modal`;
      const modalOptions = {
        component: childComponent,
        componentProps: componentProps,
        cssClass: customCssClass,
        canDismiss: this.StackModalProps.IsDismissable,
        backdropDismiss: this.StackModalProps.IsDismissable,
        initialBreakpoint: this.StackModalProps.InitialBreakpoint,
        breakpoints: this.StackModalProps.Breakpoints,
        expandToScroll: this.StackModalProps.ExpandToScroll,
        animated: modalProps.Animated ?? true,
        handle: false,
      };
      await this.openStackedModal(modalOptions);
    },
    async openModal(modalOptions: ModalOptions) {
      this.IsModalPresented = false;
      if (this.ModalRef) {
        await this.dismissModal();
      }
      this.ModalRef = await modalController.create(modalOptions);
      await this.presentModal(this.ModalRef, this.ModalProps);
    },
    async openStackedModal(modalOptions: ModalOptions) {
      this.IsModalPresented = false;
      if (this.StackModalRef) {
        await this.dismissStackedModal();
      }
      this.StackModalRef = await modalController.create(modalOptions);
      await this.presentModal(this.StackModalRef, this.StackModalProps);
    },
    async presentModal(modalRef: HTMLIonModalElement, modalProps: IonModalProps | null = null) {
      await modalRef.present();
      modalRef.onDidDismiss().then((event) => {
        this.IsModalPresented = false;
        if (this.ModalRef === modalRef) {
          this.ModalRef = null;
        }
        if (this.StackModalRef === modalRef) {
          this.StackModalRef = null;
        }
        if (modalProps && modalProps.onModalDismiss) {
          modalProps.onModalDismiss(event);
        }
      });
      this.IsModalPresented = true;
    },
    async dismissModal() {
      // The stacked modal is checked first: it can exist WITHOUT a base modal (opened
      // from a routed page), and the ModalRef early-return would leave it undismissable.
      if (this.StackModalRef) {
        await this.dismissStackedModal();
        return;
      }
      if (!this.ModalRef) {
        return;
      }
      this.ModalRef.canDismiss = true;
      await this.ModalRef.dismiss();
      this.ModalRef = null;
      this.IsModalPresented = false;
    },
    async dismissStackedModal() {
      if (!this.StackModalRef) {
        return;
      }
      this.StackModalRef.canDismiss = true;
      await this.StackModalRef.dismiss();
      this.StackModalRef = null;
      this.IsModalPresented = false;
    },
  },
});
